import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CheckCircle2,
  Circle,
  BookOpen,
  Lock,
  Crown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Extended lesson content
const lessonContent = {
  tb_1: {
    title: "Understanding Trauma Bonds",
    content: `Trauma bonds form through cycles of abuse and intermittent reinforcement. Unlike healthy attachments that develop through consistent care, trauma bonds are created when periods of kindness alternate with periods of cruelty.

This creates a powerful psychological hook because your brain becomes addicted to the "high" of the good times, especially after experiencing the "low" of abuse. The unpredictability releases dopamine in the same way gambling does.

**Key Signs of Trauma Bonding:**
- Defending your abuser to others
- Feeling unable to leave despite knowing you should
- Missing them intensely during separation
- Believing no one else could love you
- Feeling responsible for their behavior`,
    reflection: "What cycles of hot and cold did you experience in your relationship?"
  },
  tb_2: {
    title: "The Chemistry of Attachment",
    content: `Your brain releases powerful chemicals during the abuse cycle:

**Dopamine** - Released during the "honeymoon" phases, creating intense feelings of pleasure and reward. This is the same chemical released during drug use.

**Cortisol** - Stress hormone released during abuse. Over time, your brain becomes conditioned to this stress state.

**Oxytocin** - The "bonding" hormone, released during intimate moments, making you feel connected even when the relationship is harmful.

This chemical cocktail makes leaving feel physically painful, like withdrawal. It's not weakness—it's biology.`,
    reflection: "Understanding this is biological helps remove shame. What 'withdrawal' symptoms have you noticed?"
  },
  tb_3: {
    title: "Breaking the Cycle",
    content: `Breaking a trauma bond requires intention and strategy:

**1. No Contact** - This is the most powerful tool. Every interaction refreshes the trauma bond.

**2. Reality Journaling** - Write down the bad times when you're missing the good.

**3. Support System** - You can't do this alone. Lean on safe people.

**4. Time** - The chemicals need time to regulate. Most people feel significant relief after 90 days of no contact.

**5. Self-Care** - Replace the chaos with genuine peace. Your nervous system needs to learn that calm is safe.`,
    reflection: "Which of these strategies can you implement today?"
  },
  tb_4: {
    title: "Breadcrumb Detection",
    content: `Narcissists are masters at throwing "breadcrumbs"—just enough attention to keep you hooked without real commitment.

**Common Breadcrumbs:**
- Random "thinking of you" texts after silence
- Liking old social media posts
- "Accidental" contact (showing up where you are)
- Reaching out on significant dates
- Vague promises of change without action
- Hot and cold behavior—interested one day, gone the next

**Why They Do It:**
They need narcissistic supply. They want to know they still have power over you.

**How to Respond:**
Don't. Every response feeds the cycle.`,
    reflection: "Have you received breadcrumbs? How did they make you feel?"
  },
  tb_5: {
    title: "The Urge Interruption Protocol",
    content: `When you feel the urge to reach out, use this protocol:

**STOP** - Pause. Don't act immediately.

**BREATHE** - Take 5 deep breaths to activate your parasympathetic nervous system.

**IDENTIFY** - Name what you're feeling (lonely, triggered, nostalgic).

**REMEMBER** - Read your list of why you left. Remember the bad times.

**REACH OUT** - But to a safe person, not your abuser.

**WAIT** - Tell yourself you'll wait 24 hours. Most urges pass.

**CELEBRATE** - Each resisted urge is a victory.`,
    reflection: "Write down your 'why I left' list now while you're thinking clearly."
  },
  gl_1: {
    title: "What is Gaslighting?",
    content: `Gaslighting is a form of psychological manipulation where the abuser makes you question your own reality, memory, and perception.

**Common Gaslighting Phrases:**
- "That never happened"
- "You're too sensitive"
- "You're imagining things"
- "I never said that"
- "You're crazy"
- "Everyone thinks you're overreacting"

**The Goal:**
To make you doubt yourself so much that you become dependent on them to define reality.

**The Result:**
You lose trust in your own judgment, making it harder to leave because you don't trust your perception that something is wrong.`,
    reflection: "What phrases did you hear that made you doubt yourself?"
  },
  gl_2: {
    title: "Was It Real?",
    content: `Yes. Your experiences were real. Your feelings were valid.

Gaslighting makes you question this fundamental truth. But consider:

- If it wasn't real, why did it hurt so much?
- If you were just "too sensitive," why did you feel fine before this relationship?
- If nothing happened, why do you have such strong emotional reactions?

Your body knows the truth. Trauma is stored in your nervous system. The anxiety, the hypervigilance, the depression—these are evidence that something happened.

Trust your body. Trust your memories. Trust yourself.`,
    reflection: "Write down three things you know to be true about what happened."
  },
  gl_3: {
    title: "Rebuilding Self-Trust",
    content: `Learning to trust yourself again after gaslighting takes practice:

**Start Small** - Make small decisions and trust them. What to eat, what to wear.

**Journal Your Reality** - Write down what happened each day. This creates a record you can trust.

**Check with Safe People** - Ask trusted friends to validate your perceptions.

**Notice Your Body** - Learn to recognize when your body signals danger or safety.

**Affirm Yourself** - "I trust my perception. I know what I experienced."

**Forgive Yourself** - For doubting yourself. It's not your fault—you were manipulated.`,
    reflection: "What's one way you can practice trusting yourself this week?"
  },
  gl_4: {
    title: "Cognitive Reframing",
    content: `Replace distorted thoughts planted by gaslighting with truth:

**Distorted:** "I'm too sensitive"
**Truth:** "I have healthy emotional responses to unhealthy treatment"

**Distorted:** "It wasn't that bad"
**Truth:** "Minimizing my pain doesn't make it less real"

**Distorted:** "I caused his behavior"
**Truth:** "I am only responsible for my actions, not his choices"

**Distorted:** "No one else would want me"
**Truth:** "My worth is not determined by one person's inability to see it"

**Distorted:** "I'll never heal"
**Truth:** "Healing is happening, even when I can't see it"`,
    reflection: "What distorted thought do you need to reframe today?"
  },
  gl_5: {
    title: "Your Truth Matters",
    content: `Reclaiming your narrative is powerful healing work.

**Write Your Story** - Not for anyone else, just for you. What happened. How you felt. What you survived.

**Speak Your Truth** - To a therapist, a trusted friend, a support group. Being witnessed heals.

**Name the Abuse** - Calling it what it was validates your experience.

**Release Their Narrative** - You don't have to convince them. Your truth exists whether they acknowledge it or not.

**Move Forward** - Your story doesn't end with the abuse. You are writing new chapters now.

"The truth will set you free" - John 8:32`,
    reflection: "What part of your truth have you been afraid to speak?"
  },
  bb_1: {
    title: "Why Boundaries Matter",
    content: `Boundaries are not walls—they are gates you control.

Healthy boundaries:
- Protect your energy
- Define what's acceptable
- Communicate your needs
- Create space for healing
- Are an act of self-love

**Why We Struggle:**
- Taught that boundaries are selfish
- Fear of abandonment
- Guilt and obligation
- Don't know what healthy looks like

**The Truth:**
People who love you will respect your boundaries. Those who don't respect them don't respect you.`,
    reflection: "Where in your life do you feel boundaries are lacking?"
  },
  bb_2: {
    title: "Identifying Your Limits",
    content: `To set boundaries, you must first know your limits.

**Ask Yourself:**
- What drains my energy?
- What feels like a violation?
- When do I feel resentful?
- What do I need to feel safe?
- What behaviors am I no longer willing to accept?

**Physical Boundaries:** Personal space, touch, privacy

**Emotional Boundaries:** Your feelings, emotional labor, what you share

**Time Boundaries:** How you spend your time, availability

**Mental Boundaries:** Your thoughts, opinions, beliefs

**Material Boundaries:** Your possessions, money, space`,
    reflection: "List three non-negotiable boundaries you need to establish."
  },
  bb_3: {
    title: "Script Templates",
    content: `Having pre-written responses helps when you're caught off guard:

**When They Cross a Boundary:**
"I'm not comfortable with that. Please stop."
"That doesn't work for me."
"I've told you how I feel about this. The conversation is over."

**When They Push Back:**
"I understand you disagree. My boundary remains the same."
"I'm not asking for permission. I'm informing you."
"Your feelings about my boundaries are not my responsibility."

**When They Guilt You:**
"I can love you and still have boundaries."
"Protecting my peace is not cruel."
"I'm sorry you feel that way. My answer is still no."

**For No Contact:**
"I've chosen not to continue contact. Please respect my decision."
Then: Block. Delete. No response to any attempts.`,
    reflection: "Which script resonates most with your current situation?"
  },
  bb_4: {
    title: "Guilt Dismantling",
    content: `Guilt is often a sign you've been trained to ignore your needs.

**Unhealthy Guilt:**
- Feeling bad for having needs
- Feeling responsible for others' emotions
- Believing you're "mean" for having boundaries
- Fear that you're being unfair

**Truth Bombs:**
- Having needs doesn't make you needy
- You can't control others' reactions
- Boundaries are kind, not cruel
- Fairness doesn't mean sacrificing yourself

**The Root:**
Often, guilt comes from childhood conditioning or manipulation. It's learned—and can be unlearned.

"You're not guilty for protecting your peace."`,
    reflection: "Where did you learn that having boundaries was wrong?"
  },
  bb_5: {
    title: "Boundary Rehearsal",
    content: `Practice makes protection.

**Why Rehearse:**
- Reduces anxiety about confrontation
- Builds confidence
- Helps you stay calm under pressure
- Makes boundaries feel more natural

**How to Practice:**
1. Identify a boundary you need to set
2. Write out what you want to say
3. Say it out loud—multiple times
4. Practice with a trusted friend
5. Visualize staying calm when they react
6. Remember: their reaction is not your responsibility

**After Setting Boundaries:**
- Don't over-explain
- Don't apologize for your needs
- Follow through with consequences
- Celebrate your courage`,
    reflection: "What boundary will you rehearse this week?"
  },
  ns_1: {
    title: "Understanding Your Nervous System",
    content: `Your body keeps score of trauma.

**The Autonomic Nervous System:**

**Sympathetic (Fight/Flight):**
- Heart racing
- Shallow breathing
- Muscle tension
- Hypervigilance

**Parasympathetic (Rest/Digest):**
- Calm heartbeat
- Deep breathing
- Relaxed muscles
- Feeling safe

**Freeze Response:**
When fight or flight doesn't work, the body shuts down.

**After Narcissistic Abuse:**
Your nervous system may be stuck in survival mode, constantly scanning for danger. This isn't anxiety—it's a protective response that served you.

Healing means teaching your body it's safe again.`,
    reflection: "Where do you feel stress in your body? Notice it without judgment."
  },
  ns_2: {
    title: "60-Second Grounding",
    content: `Quick techniques to bring you back to center:

**5-4-3-2-1 Technique:**
- Name 5 things you see
- Name 4 things you feel (touch)
- Name 3 things you hear
- Name 2 things you smell
- Name 1 thing you taste

**Square Breathing:**
- Breathe in for 4 counts
- Hold for 4 counts
- Breathe out for 4 counts
- Hold for 4 counts

**Physical Grounding:**
- Press feet firmly into floor
- Squeeze and release fists
- Splash cold water on face
- Hold ice cubes

**Quick Reset:**
Place hand on heart, say: "I am safe. I am here. This moment is all that exists."`,
    reflection: "Practice 5-4-3-2-1 right now. How do you feel after?"
  },
  ns_3: {
    title: "Breathwork for Peace",
    content: `Your breath is the remote control for your nervous system.

**Calming Breath (4-7-8):**
- Inhale through nose: 4 counts
- Hold: 7 counts
- Exhale through mouth: 8 counts
- Repeat 4 times

**Physiological Sigh:**
- Double inhale through nose (sniff-sniff)
- Long exhale through mouth
- Most effective single technique for calming

**Box Breathing:**
- Equal counts: inhale, hold, exhale, hold
- Start with 4 counts each

**When to Use:**
- Before difficult conversations
- When triggered
- Before sleep
- During anxiety
- Any time you need reset`,
    reflection: "Try the physiological sigh three times. Notice the shift."
  },
  ns_4: {
    title: "Panic to Peace Protocol",
    content: `What to do when anxiety hits hard:

**Step 1: Name It**
"I am having a panic response. It will pass."

**Step 2: Ground Physically**
- Feet on floor
- Hands pressing against something solid
- Cold water on wrists

**Step 3: Breathe**
Slow exhales are key. 4 counts in, 8 counts out.

**Step 4: Move**
Walk, shake your body, release the energy.

**Step 5: Self-Compassion**
"This is hard. It's okay to struggle. I am safe right now."

**Step 6: Distraction**
Name all the colors you see. Count backward from 100 by 7s.

**Remember:**
Panic peaks and passes. It cannot hurt you. You've survived every panic attack so far.`,
    reflection: "What helps you most during intense anxiety?"
  },
  ns_5: {
    title: "Daily Nervous System Care",
    content: `Building a routine for regulation:

**Morning:**
- Don't check phone immediately
- Gentle stretching
- Calm morning routine
- Intention setting

**Throughout Day:**
- Regular meal times
- Short breaks for breathing
- Movement (even 5 minutes)
- Limiting triggers (news, social media)

**Evening:**
- Screen-free time before bed
- Gentle routine
- Gratitude reflection
- Relaxing activity

**Weekly:**
- Time in nature
- Connection with safe people
- Something creative
- Rest without guilt

**Your nervous system needs:**
Routine, rest, safety, connection, and gentleness.`,
    reflection: "What one thing can you add to your daily routine for nervous system care?"
  }
};

export default function ModuleDetail() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLesson, setExpandedLesson] = useState(null);
  const isPremium = user?.is_premium;

  useEffect(() => {
    fetchModule();
  }, [moduleId]);

  const fetchModule = async () => {
    try {
      const res = await axios.get(`${API}/modules/${moduleId}`);
      setModule(res.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  const toggleLessonComplete = async (lessonId, currentlyCompleted) => {
    try {
      await axios.post(`${API}/modules/progress`, {
        module_id: moduleId,
        lesson_id: lessonId,
        completed: !currentlyCompleted
      });
      
      // Update local state
      setModule(prev => ({
        ...prev,
        lessons: prev.lessons.map(l => 
          l.id === lessonId ? { ...l, completed: !currentlyCompleted } : l
        )
      }));
      
      if (!currentlyCompleted) {
        toast.success('Lesson completed! Keep going, Queen.');
      }
    } catch (error) {
      toast.error('Failed to update progress');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!module) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-muted-foreground">Module not found</p>
          <Link to="/modules" className="text-primary hover:underline">Back to Modules</Link>
        </div>
      </Layout>
    );
  }

  const completedCount = module.lessons.filter(l => l.completed).length;
  const progress = (completedCount / module.lessons.length) * 100;

  return (
    <Layout>
      <div className="p-6 lg:p-10 max-w-4xl mx-auto" data-testid="module-detail-page">
        {/* Back Link */}
        <Link 
          to="/modules" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Modules
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-foreground mb-3">
            {module.title}
          </h1>
          <p className="text-muted-foreground mb-6">{module.description}</p>
          
          {/* Progress Bar */}
          <div className="crown-card p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">{completedCount} of {module.lessons.length} lessons completed</span>
              <span className="font-semibold text-primary">{Math.round(progress)}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-3">
              <div 
                className="bg-primary h-3 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Lessons Accordion */}
        <Accordion 
          type="single" 
          collapsible 
          value={expandedLesson}
          onValueChange={(val) => {
            // Check if lesson is locked
            const lesson = module.lessons.find(l => l.id === val);
            if (lesson?.locked) {
              toast.error('This lesson requires Premium');
              return;
            }
            setExpandedLesson(val);
          }}
          className="space-y-4"
        >
          {module.lessons.map((lesson, idx) => {
            const content = lessonContent[lesson.id] || { 
              title: lesson.title, 
              content: lesson.content,
              reflection: "Take a moment to reflect on what you've learned."
            };
            const isLocked = lesson.locked;
            
            return (
              <AccordionItem 
                key={lesson.id} 
                value={lesson.id}
                className={`crown-card overflow-hidden border-0 ${isLocked ? 'opacity-70' : ''}`}
                data-testid={`lesson-${lesson.id}`}
                disabled={isLocked}
              >
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center gap-4 text-left w-full">
                    {isLocked ? (
                      <Lock className="w-6 h-6 text-muted-foreground flex-shrink-0" />
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isLocked) toggleLessonComplete(lesson.id, lesson.completed);
                        }}
                        className="flex-shrink-0"
                        data-testid={`lesson-checkbox-${lesson.id}`}
                      >
                        {lesson.completed ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-muted-foreground" />
                        )}
                      </button>
                    )}
                    <div className="flex-1">
                      <span className="text-sm text-muted-foreground">Lesson {idx + 1}</span>
                      <h3 className={`font-semibold ${lesson.completed ? 'text-muted-foreground' : 'text-foreground'}`}>
                        {content.title}
                      </h3>
                    </div>
                    {isLocked && (
                      <span className="px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium">
                        Premium
                      </span>
                    )}
                  </div>
                </AccordionTrigger>
                {!isLocked && (
                <AccordionContent className="px-6 pb-6">
                  <div className="pl-10 space-y-6">
                    {/* Main Content */}
                    <div className="prose prose-sm max-w-none text-foreground whitespace-pre-line">
                      {content.content}
                    </div>
                    
                    {/* Reflection */}
                    <div className="glass-gold rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-700 dark:text-amber-300">Reflection</span>
                      </div>
                      <p className="text-foreground italic">{content.reflection}</p>
                    </div>
                    
                    {/* Mark Complete Button */}
                    {!lesson.completed && (
                      <Button
                        onClick={() => toggleLessonComplete(lesson.id, false)}
                        className="rounded-full bg-primary hover:bg-primary/90"
                        data-testid={`complete-btn-${lesson.id}`}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Mark as Complete
                      </Button>
                    )}
                  </div>
                </AccordionContent>
                )}
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* Premium Upgrade CTA for free users */}
        {!isPremium && (
          <div className="mt-8 crown-card p-6 text-center border-amber-300/50">
            <Lock className="w-8 h-8 text-amber-500 mx-auto mb-3" />
            <h3 className="font-semibold text-foreground mb-2">Unlock All Lessons</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Upgrade to Premium to access all {module.lessons.length} lessons in this module.
            </p>
            <Button 
              onClick={() => navigate('/subscription')}
              className="btn-gold rounded-full px-6"
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade to Premium
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
